import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { LogIn, Store, ShieldCheck, ExternalLink } from "lucide-react";
import { SITE } from "@/constants/site";
import { cn } from "@/lib/utils";

// Distinct "Partner Login" button. For now it opens a "Coming Soon" dialog.
// When SITE.partnerPortalReady is flipped to true it becomes a real external
// link to the admin subdomain (admin.halalgo.ph).
export const PartnerLoginButton = ({ className = "", fullWidth = false }) => {
  const [open, setOpen] = useState(false);

  const base =
    "border border-halalgo-kraft-300 bg-white text-halalgo-ink shadow-inset-kraft transition-colors hover:bg-halalgo-kraft-50 active:scale-[0.98]";

  if (SITE.partnerPortalReady) {
    return (
      <Button
        asChild
        variant="outline"
        className={cn(base, fullWidth && "w-full", className)}
        data-testid="nav-partner-login-button"
      >
        <a href={SITE.partnerPortalUrl} target="_blank" rel="noopener noreferrer">
          <LogIn className="h-4 w-4" /> Partner Login
        </a>
      </Button>
    );
  }

  return (
    <>
      <Button
        variant="outline"
        className={cn(base, fullWidth && "w-full", className)}
        data-testid="nav-partner-login-button"
        onClick={() => setOpen(true)}
      >
        <LogIn className="h-4 w-4" /> Partner Login
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md" data-testid="partner-login-dialog">
          <DialogHeader>
            <div className="mb-2 flex h-12 w-12 items-center justify-center rounded-xl bg-halalgo-green-50 text-halalgo-green-700 ring-1 ring-halalgo-green-200">
              <Store className="h-6 w-6" />
            </div>
            <div className="flex items-center gap-2">
              <DialogTitle className="text-xl">Partner Portal</DialogTitle>
              <Badge className="rounded-md border-transparent bg-halalgo-kraft-500 text-halalgo-ink hover:bg-halalgo-kraft-500">
                Coming Soon
              </Badge>
            </div>
            <DialogDescription className="pt-1 text-halalgo-ink/70">
              Our partner dashboard for restaurants, kitchens and riders is on the
              way. You'll manage menus, orders and deliveries here — all Halal, all
              in one place.
            </DialogDescription>
          </DialogHeader>

          <div className="mt-2 flex items-center gap-2 rounded-lg border border-halalgo-kraft-100 bg-halalgo-paper2 px-3 py-2.5 text-sm text-halalgo-ink/75">
            <ShieldCheck className="h-4 w-4 shrink-0 text-halalgo-green-700" />
            <span>
              The portal will live at{" "}
              <span className="font-semibold text-halalgo-ink">admin.halalgo.ph</span>
            </span>
          </div>

          <DialogFooter className="mt-4 gap-2 sm:gap-2">
            <Button
              disabled
              className="bg-halalgo-green-600 text-white opacity-60"
              data-testid="partner-portal-open-disabled"
            >
              <ExternalLink className="h-4 w-4" /> Open Partner Portal
            </Button>
            <DialogClose asChild>
              <Button variant="outline" className="border-halalgo-kraft-200" data-testid="partner-dialog-close">
                Got it
              </Button>
            </DialogClose>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
